import React, { useState, useEffect } from 'react';
import { useBilling } from '../hooks/useBilling';
import { safeParseNumber } from '../types';
import type { BillingHistory as BillingHistoryType } from '../types';
import './BillingHistory.css';

const BillingHistory: React.FC = () => {
  const { getBillingHistory } = useBilling();
  const [history, setHistory] = useState<BillingHistoryType | null>(null);
  const [loading, setLoading] = useState(true);
  const [page] = useState(1);

  useEffect(() => {
    const loadHistory = async () => {
      setLoading(true);
      const data = await getBillingHistory(page, 20);
      if (data) setHistory(data);
      setLoading(false);
    };
    loadHistory();
  }, [getBillingHistory, page]);

  const filteredHistory = history?.history.filter(transaction => {
    // Filter out payment_recovery events (Payment failure resolved - billing re-enabled)
    if (transaction.event_type === 'payment_recovery') return false;
    
    // Filter out transactions with zero or undefined amounts
    const amount = safeParseNumber(transaction.amount);
    if (amount === 0) return false;
    
    return true;
  }) || [];

  if (loading) {
    return (
      <div className="card">
        <div className="card-header">
          <h2>Transaction History</h2>
        </div>
        <div className="card-body">
          <div className="loading-section">
            <div className="loading-spinner"></div>
            <p>Loading transactions...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2>Transaction History</h2>
      </div>

      <div className="card-body" style={{ padding: 0 }}>
        {filteredHistory.length === 0 ? (
          <div className="empty-section">
            <h4>No transactions yet</h4>
            <p>Your transaction history will appear here</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="table-premium">
              <thead>
                <tr>
                  <th style={{ width: '200px' }}>Transaction</th>
                  <th style={{ width: '100px' }}>Contacts</th>
                  <th style={{ width: '120px' }}>Amount</th>
                  <th style={{ width: '140px' }}>Date</th>
                  <th style={{ width: '120px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.map((transaction, index) => {
                  const amount = safeParseNumber(transaction.amount);
                  
                  // Create simple, user-friendly descriptions
                  const getSimpleDescription = (description: string) => {
                    if (description.includes('payment for') && description.includes('contacts')) {
                      const match = description.match(/payment for (\d+) contacts/i);
                      if (match) {
                        const contactCount = parseInt(match[1]);
                        const formattedCount = contactCount.toLocaleString();
                        
                        // Check if there's debt rollover by comparing amount to expected contact cost
                        // Typical cost is $0.02 per contact
                        const expectedCost = contactCount * 0.02;
                        const actualAmount = Math.abs(amount);
                        const hasDebtRollover = actualAmount > (expectedCost + 0.01); // Small buffer for rounding
                        
                        if (hasDebtRollover) {
                          return `Paid for ${formattedCount} contacts + credits`;
                        }
                        
                        return `Paid for ${formattedCount} contacts`;
                      }
                      return `Paid for contacts`;
                    }
                    if (description.includes('Unlocked') && description.includes('contacts')) {
                      const match = description.match(/Unlocked (\d+) contacts/);
                      const contactCount = match ? parseInt(match[1]).toLocaleString() : 'contacts';
                      return `Used credits for ${contactCount} contacts`;
                    }
                    if (description.includes('One-time credit')) {
                      return 'Added payment method credit';
                    }
                    if (description.includes('Payment method added')) {
                      return 'Added payment method';
                    }
                    if (description.includes('Payment processed:')) {
                      return 'Payment processed';
                    }
                    return 'Transaction';
                  };
                  
                  // Extract contact count for the contacts column
                  const getContactCount = (description: string) => {
                    if (description.includes('contacts')) {
                      const match = description.match(/(\d+) contacts/);
                      return match ? match[1] : null;
                    }
                    return null;
                  };
                  
                  const contactCount = getContactCount(transaction.description);
                  
                  return (
                    <tr key={`${transaction.timestamp}-${index}`}>
                      <td>
                        <div className="flex flex-col gap-1">
                          <div className="font-medium text-gray-900">
                            {getSimpleDescription(transaction.description)}
                          </div>
                          {transaction.stripe_charge_id && (
                            <div className="text-xs text-gray-500">
                              Receipt: {transaction.stripe_charge_id}
                            </div>
                          )}
                        </div>
                      </td>
                      
                      <td>
                        <div className="text-right">
                          {contactCount ? (
                            <span className="text-sm font-medium text-gray-700">
                              {parseInt(contactCount).toLocaleString()}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </div>
                      </td>
                      
                      <td>
                        <div className="text-right">
                          {amount !== 0 ? (
                            <span className="text-sm font-medium text-gray-900">
                              ${Math.abs(amount).toFixed(2)}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </div>
                      </td>
                      
                      <td>
                        <div className="flex flex-col gap-1">
                          <div>
                            <span className="font-medium text-sm">
                              {new Date(transaction.timestamp).toLocaleDateString()}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(transaction.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                      </td>
                      
                      <td className="status-column">
                        <span className={`status-badge ${transaction.status}`}>
                          {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingHistory;